/**
 * FokusButton Component (Shared)
 * 
 * Smart dropdown button for FOKUS mode - used across Flow, Kanban, and Table views.
 * Click = Focus selected, Dropdown = Filter options that trigger FOKUS
 * 
 * FOKUS MODE shows:
 * - Selected/filtered task(s) - the "target"
 * - ALL incomplete upstream dependencies (recursively)
 * - +1 downstream (immediate children only)
 */

import React, { useRef, useCallback, useState, useMemo } from 'react';
import { ChevronIcon } from '../filterUtils';
import { BrainIcon, ExitIcon, FokusDropdownMenu, FokusGradientDef } from './components';
import FokusEmptySelectionModal from './components/FokusEmptySelectionModal';
import { useFokusFilters, useFokusDropdown } from './hooks';
import { isTaskAvailable, isTaskActive } from '../utils/taskAvailabilityUtils';
import './styles/index.css';

const FokusButton = ({
  isFocusMode,
  hasSelection,
  selectedCount = 0,
  actionableTaskIds = [],
  onFocusSelected,
  onExitFocusMode,
  onFilterFocus,
  tasks = [],
  categories = [],
  statuses = [],
  allTags = [],
  lockStates = {},
  nestedSprints = [],
  // Customization for different views
  selectionLabel = 'Selected',
}) => {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [emptyModalOpen, setEmptyModalOpen] = useState(false);

  const availableTaskIds = useMemo(
    () =>
      tasks
        .filter((task) => isTaskAvailable(task, lockStates, nestedSprints, statuses))
        .map((task) => task.id),
    [tasks, lockStates, nestedSprints, statuses],
  );

  const inProgressTaskIds = useMemo(
    () =>
      tasks
        .filter((task) => isTaskActive(task, nestedSprints, statuses))
        .map((task) => task.id),
    [tasks, nestedSprints, statuses],
  );

  // Custom hooks
  const filters = useFokusFilters({
    tasks,
    categories,
    statuses,
    allTags,
    isFocusMode,
    onFilterFocus,
  });

  const dropdown = useFokusDropdown({ buttonRef });

  // Handle main button click (focus selected, empty-selection modal, or exit)
  const handleMainClick = useCallback(() => {
    if (isFocusMode) {
      filters.clearAllFilters();
      onExitFocusMode();
    } else if (hasSelection) {
      filters.clearAllFilters();
      onFocusSelected();
    } else {
      dropdown.setIsOpen(false);
      setEmptyModalOpen(true);
    }
  }, [isFocusMode, hasSelection, onFocusSelected, onExitFocusMode, filters, dropdown]);

  const handleEmptyModalConfirm = useCallback(
    (taskIdSet) => {
      filters.clearAllFilters();
      onFocusSelected(taskIdSet);
    },
    [filters, onFocusSelected],
  );

  // Handle actionable click
  const handleActionableClick = useCallback(() => {
    if (actionableTaskIds.length > 0) {
      filters.clearAllFilters();
      onFilterFocus(actionableTaskIds);
      dropdown.setIsOpen(false);
    }
  }, [actionableTaskIds, onFilterFocus, filters, dropdown]);

  return (
    <div className="fokus-button" ref={buttonRef}>
      {/* SVG gradient definition for icon stroke */}
      <FokusGradientDef />
      
      {/* Main button area */}
      <div className="fokus-button__container">
        {isFocusMode ? (
          // Exit mode
          <button
            className="fokus-button__main fokus-button__main--exit"
            onClick={handleMainClick}
            title="Exit Fokus mode"
          >
            <ExitIcon />
            <span className="fokus-button__label">Exit Fokus</span>
          </button>
        ) : (
          // Normal mode - main button + dropdown arrow
          <>
            <div className="fokus-button__main-wrapper">
              <button
                className="fokus-button__main"
                onClick={handleMainClick}
                title={hasSelection ? `Focus on ${selectedCount} selected` : 'Fokus'}
              >
                <BrainIcon />
                <span className="fokus-button__label">Fokus</span>
              </button>
            </div>
            
            <button
              className={`fokus-button__dropdown-toggle ${dropdown.isOpen ? 'fokus-button__dropdown-toggle--open' : ''}`}
              onClick={() => dropdown.setIsOpen(!dropdown.isOpen)}
              title="Fokus options"
            >
              <ChevronIcon />
            </button>
          </>
        )}
      </div>

      {/* Dropdown menu */}
      {dropdown.isOpen && !isFocusMode && (
        <FokusDropdownMenu
          menuRef={menuRef}
          menuPosition={dropdown.menuPosition}
          // Quick actions
          hasSelection={hasSelection}
          selectedCount={selectedCount}
          selectionLabel={selectionLabel}
          actionableTaskIds={actionableTaskIds}
          onFocusSelected={onFocusSelected}
          onActionableClick={handleActionableClick}
          clearAllFilters={filters.clearAllFilters}
          setIsOpen={dropdown.setIsOpen}
          // Filter state
          activeFilterCount={filters.activeFilterCount}
          matchingTaskIds={filters.matchingTaskIds}
          expandedSections={dropdown.expandedSections}
          toggleSection={dropdown.toggleSection}
          // Status
          statusOptions={filters.statusOptions}
          selectedStatuses={filters.selectedStatuses}
          toggleStatus={filters.toggleStatus}
          // Categories
          taskCategories={filters.taskCategories}
          selectedCategories={filters.selectedCategories}
          toggleCategory={filters.toggleCategory}
          // Tags
          taskTags={filters.taskTags}
          selectedTags={filters.selectedTags}
          toggleTag={filters.toggleTag}
          // Priority
          selectedPriorities={filters.selectedPriorities}
          togglePriority={filters.togglePriority}
          // Due Date
          selectedDueDates={filters.selectedDueDates}
          toggleDueDate={filters.toggleDueDate}
        />
      )}

      <FokusEmptySelectionModal
        isOpen={emptyModalOpen}
        onClose={() => setEmptyModalOpen(false)}
        onConfirm={handleEmptyModalConfirm}
        availableTaskIds={availableTaskIds}
        inProgressTaskIds={inProgressTaskIds}
      />
    </div>
  );
};

export default FokusButton;
